import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../services/firebase";
import { Globals } from "../globals";
import { deadlineState, DeletionStatus, dueDateOf, normaliseStatus } from "../utils/deletionRequests";
import Table from "react-bootstrap/esm/Table";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Badge from "react-bootstrap/esm/Badge";
import Card from "react-bootstrap/esm/Card";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import Modal from "react-bootstrap/esm/Modal";

interface DeletionRequestRow {
    id: string;
    email: string;
    requestedAt: Date | null;
    status: DeletionStatus;
    resolvedAt: Date | null;
    note?: string;
}

/** What findDeletionRequestData returns (functions/src/deletionRequests.ts). */
interface PersonFindings {
    email: string;
    preachers: {
        gemeindeId: string;
        gemeindeName: string;
        archived: boolean;
        textMentions: number;
        plan: {
            preacherKey: string;
            name: string;
            absences: number;
            accessDocs: number;
            tokens: number;
            swaps: number;
            futureSlots: { serviceId: string; date: string; slot: 1 | 2 }[];
            pastSlots: number;
        };
    }[];
    conductorIn: { gemeindeId: string; gemeindeName: string }[];
    gemeindeContactIn: { gemeindeId: string; gemeindeName: string }[];
    account: { uid: string; role: string | null; gemeindeId: string | null; protected: boolean } | null;
}

interface Props {
    notify: (variant: string, message: string) => void;
}

const STATUS_VARIANT: Record<DeletionStatus, string> = {
    open: "warning",
    in_progress: "info",
    done: "success",
    rejected: "secondary",
};

const toDate = (value: unknown) => (value instanceof Timestamp ? value.toDate() : null);

/**
 * GDPR deletion requests (GOD-32): list with status and deadline, search of the
 * address across all Gemeinden, and the deletion itself after confirmation.
 */
export default function DeletionRequests({ notify }: Props) {
    const { t } = useTranslation();
    const locale = t("calendar.locale") as string;
    const [rows, setRows] = useState<DeletionRequestRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<DeletionRequestRow | null>(null);
    const [findings, setFindings] = useState<PersonFindings | null>(null);
    const [searching, setSearching] = useState(false);
    const [clearFutureSlots, setClearFutureSlots] = useState(false);
    const [deleteAccount, setDeleteAccount] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [executing, setExecuting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, Globals.DATA_DELETION_REQUESTS_COLLECTION));
            const list = snap.docs.map((d) => {
                const data = d.data();
                return {
                    id: d.id,
                    email: String(data.email ?? data.preacherEmail ?? ""),
                    requestedAt: toDate(data.requestedAt),
                    status: normaliseStatus(data.status),
                    resolvedAt: toDate(data.resolvedAt),
                    note: data.note,
                } as DeletionRequestRow;
            });
            list.sort((a, b) => (b.requestedAt?.getTime() ?? 0) - (a.requestedAt?.getTime() ?? 0));
            setRows(list);
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setLoading(false);
    }, [notify, t]);

    useEffect(() => {
        load();
    }, [load]);

    const formatDate = (date: Date | null) => (date ? date.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) : "–");

    async function setStatus(row: DeletionRequestRow, status: DeletionStatus) {
        try {
            const update = httpsCallable(functions, "updateDeletionRequest");
            await update({ requestId: row.id, status });
            await load();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
    }

    async function search(row: DeletionRequestRow) {
        setSelected(row);
        setFindings(null);
        setClearFutureSlots(false);
        setDeleteAccount(true);
        setSearching(true);
        try {
            const find = httpsCallable<{ requestId: string }, PersonFindings>(functions, "findDeletionRequestData");
            setFindings((await find({ requestId: row.id })).data);
            if (row.status === "open") await setStatus(row, "in_progress");
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setSearching(false);
    }

    async function execute() {
        if (!selected) return;
        setExecuting(true);
        try {
            const run = httpsCallable<object, { preachers: number; conductorContacts: number; accountDeleted: boolean }>(
                functions,
                "executeDeletionRequest"
            );
            const { data } = await run({ requestId: selected.id, confirm: true, clearFutureSlots, deleteAccount });
            notify("success", t("deletionRequests.executed", data));
            setConfirming(false);
            setSelected(null);
            setFindings(null);
            await load();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setExecuting(false);
    }

    const futureSlots = findings?.preachers.flatMap((p) => p.plan.futureSlots) ?? [];
    const nothingFound =
        !!findings && findings.preachers.length === 0 && findings.conductorIn.length === 0 && !findings.account;
    const canExecute = !!findings && !nothingFound && (futureSlots.length === 0 || clearFutureSlots);

    if (loading) return <Spinner animation="border" />;

    return (
        <>
            <p className="text-muted small">{t("deletionRequests.intro")}</p>
            {rows.length === 0 && <Alert variant="light">{t("deletionRequests.none")}</Alert>}
            {rows.length > 0 && (
                <Table striped bordered hover responsive data-testid="deletion-requests">
                    <thead>
                        <tr>
                            <th>{t("deletionRequests.received")}</th>
                            <th>{t("deletionRequests.email")}</th>
                            <th>{t("deletionRequests.status")}</th>
                            <th>{t("deletionRequests.deadline")}</th>
                            <th>{t("admin.actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const due = row.requestedAt ? dueDateOf(row.requestedAt) : null;
                            const deadline = due ? deadlineState(row.status, due, new Date()) : null;
                            return (
                                <tr key={row.id} className={selected?.id === row.id ? "table-active" : undefined}>
                                    <td>{formatDate(row.requestedAt)}</td>
                                    <td>{row.email}</td>
                                    <td style={{ minWidth: "170px" }}>
                                        <Form.Select
                                            size="sm"
                                            value={row.status}
                                            onChange={(e) => setStatus(row, e.target.value as DeletionStatus)}
                                            aria-label={t("deletionRequests.status") as string}
                                        >
                                            {(["open", "in_progress", "done", "rejected"] as DeletionStatus[]).map((s) => (
                                                <option key={s} value={s}>
                                                    {t(`deletionRequests.statuses.${s}`)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Badge bg={STATUS_VARIANT[row.status]} className="mt-1">
                                            {t(`deletionRequests.statuses.${row.status}`)}
                                        </Badge>
                                    </td>
                                    <td>
                                        {formatDate(due)}
                                        {deadline && deadline.kind === "overdue" && (
                                            <Badge bg="danger" className="ms-2">
                                                {t("deletionRequests.overdue", { count: deadline.days })}
                                            </Badge>
                                        )}
                                        {deadline && deadline.kind === "open" && (
                                            <div className={deadline.days <= 7 ? "text-danger small" : "text-muted small"}>
                                                {t("deletionRequests.daysLeft", { count: deadline.days })}
                                            </div>
                                        )}
                                        {row.resolvedAt && (
                                            <div className="text-muted small">
                                                {t("deletionRequests.resolvedOn", { date: formatDate(row.resolvedAt) })}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <Button size="sm" variant="outline-primary" onClick={() => search(row)} disabled={searching}>
                                            {t("deletionRequests.search")}
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            {selected && (
                <Card className="mt-3" data-testid="deletion-findings">
                    <Card.Body>
                        <Card.Title>{t("deletionRequests.findingsTitle", { email: selected.email })}</Card.Title>
                        {searching && <Spinner animation="border" size="sm" />}
                        {findings && nothingFound && <Alert variant="info">{t("deletionRequests.nothingFound")}</Alert>}
                        {findings && !nothingFound && (
                            <ul>
                                {findings.preachers.map((p) => (
                                    <li key={`${p.gemeindeId}/${p.plan.preacherKey}`}>
                                        {t("deletionRequests.preacherIn", { name: p.plan.name, gemeinde: p.gemeindeName })}
                                        {p.archived && ` (${t("admin.archivedBadge")})`}
                                        {": "}
                                        {t("deletionRequests.preacherDetails", {
                                            absences: p.plan.absences,
                                            access: p.plan.accessDocs,
                                            tokens: p.plan.tokens,
                                            swaps: p.plan.swaps,
                                            past: p.plan.pastSlots,
                                        })}
                                        {p.textMentions > 0 && (
                                            <div className="small text-warning">
                                                {t("deletionRequests.textMentions", { count: p.textMentions })}
                                            </div>
                                        )}
                                    </li>
                                ))}
                                {findings.conductorIn.map((c) => (
                                    <li key={`conductor-${c.gemeindeId}`}>{t("deletionRequests.conductorIn", { gemeinde: c.gemeindeName })}</li>
                                ))}
                                {findings.account && (
                                    <li>
                                        {findings.account.protected
                                            ? t("deletionRequests.accountProtected")
                                            : t("deletionRequests.account", { role: findings.account.role ?? "–" })}
                                    </li>
                                )}
                            </ul>
                        )}
                        {findings && findings.gemeindeContactIn.length > 0 && (
                            <Alert variant="warning" className="small">
                                {t("deletionRequests.contactManual", {
                                    gemeinden: findings.gemeindeContactIn.map((g) => g.gemeindeName).join(", "),
                                })}
                            </Alert>
                        )}
                        {futureSlots.length > 0 && (
                            <Form.Check
                                id="deletion-clear-slots"
                                checked={clearFutureSlots}
                                onChange={(e) => setClearFutureSlots(e.target.checked)}
                                label={t("deletionRequests.clearSlots", { count: futureSlots.length })}
                            />
                        )}
                        {findings?.account && !findings.account.protected && (
                            <Form.Check
                                id="deletion-delete-account"
                                checked={deleteAccount}
                                onChange={(e) => setDeleteAccount(e.target.checked)}
                                label={t("deletionRequests.deleteAccount")}
                            />
                        )}
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="danger" disabled={!canExecute || executing} onClick={() => setConfirming(true)}>
                                {t("deletionRequests.execute")}
                            </Button>
                            {findings && nothingFound && (
                                <Button variant="outline-success" onClick={() => setStatus(selected, "done").then(() => setSelected(null))}>
                                    {t("deletionRequests.markDone")}
                                </Button>
                            )}
                            <Button variant="outline-secondary" onClick={() => setSelected(null)}>
                                {t("common.close")}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            <Modal show={confirming} onHide={executing ? undefined : () => setConfirming(false)} centered>
                <Modal.Header closeButton={!executing}>
                    <Modal.Title>{t("deletionRequests.confirmTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t("deletionRequests.confirmText", { email: selected?.email ?? "" })}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirming(false)} disabled={executing}>
                        {t("common.cancel")}
                    </Button>
                    <Button variant="danger" onClick={execute} disabled={executing}>
                        {executing ? <Spinner animation="border" size="sm" /> : t("deletionRequests.execute")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
